import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EyeTwoTone,
  HomeOutlined,
  MailOutlined,
  PhoneOutlined,
  UserAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Drawer, Descriptions, Tag, Divider, Button } from "antd";
import { fetchRoles } from "../../../redux/Roles/actions";
import { fetchCompanies } from "../../../redux/Company/actions";
import { fetchCountries } from "../../../redux/Country/actions";
import { fetchOverrides } from "../../../redux/Overrides/actions";

const ShowUser = ({ item, logo }) => {
  const [modelVisible, setModelVisible] = useState(false);
  const dispatch = useDispatch();

  // Fetch roles, companies, countries, and overrides data when the component mounts
  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchCompanies());
    dispatch(fetchCountries());
    dispatch(fetchOverrides());
  }, [dispatch]);

  // Selector to get data from the Redux store
  const { roles } = useSelector((state) => state.roles) || { roles: [] };
  const { companies } = useSelector((state) => state.companies) || {
    companies: [],
  };
  const { countries } = useSelector((state) => state.countries) || {
    countries: [],
  };
  const { overrides } = useSelector((state) => state.overrides) || {
    overrides: [],
  };

  // Find user-specific data
  const userRoles = roles.filter((role) => item?.roles?.includes(role._id)) || [];
  const userCompany =
    companies.find((company) => company._id === item?.company) || {};
  const userCountry =
    countries.find((country) => country._id === item?.country) || {};
  const userOverrides =
    overrides.filter((override) => item?.overrides?.includes(override._id)) || [];
    
  // Control drawer visibility
  const showModal = () => {
    setModelVisible(true);
  };

  const hideModalCancel = () => {
    setModelVisible(false);
  };

  return (
    <>
      {logo ? (
        <Button type="link" onClick={showModal}>
          <UserOutlined /> View User Info
        </Button>
      ) : (
        <EyeTwoTone key="view" onClick={showModal} />
      )}
      <Drawer
        title={item.username ? `Details for ${item.username}` : "User Details"}
        visible={modelVisible}
        closable={true}
        placement={window.innerWidth <= 500 ? "top" : "right"}
        onClose={hideModalCancel}
        width={window.innerWidth <= 500 ? "100%" : 600}
      >
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Username">
            <UserAddOutlined style={{ marginRight: 8 }} />
            {item.username || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            <MailOutlined style={{ marginRight: 8 }} />
            {item.email || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            <PhoneOutlined style={{ marginRight: 8 }} />
            {item.phone || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Address">
            <HomeOutlined style={{ marginRight: 8 }} />
            {item.address || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Company">
            {userCompany ? (
              <>
                <strong>Name:</strong> {userCompany.name || "N/A"} <br />
                <strong>Address:</strong> {userCompany.address || "N/A"} <br />
                <strong>URL:</strong>{" "}
                {userCompany.url ? (
                  <a
                    href={userCompany.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {userCompany.url}
                  </a>
                ) : (
                  "N/A"
                )}{" "}
                <br />
                <strong>Description:</strong> {userCompany.description || "N/A"}{" "}
                <br />
                <strong>Country:</strong>{" "}
                {userCompany.country
                  ? userCompany.country.name || "N/A"
                  : "N/A"}
              </>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Country">
            {userCountry ? userCountry.name || "N/A" : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Roles">
            {userRoles.length > 0
              ? userRoles.map((role, index) => (
                  <div key={index}>
                    <Divider orientation="left">{role.name || "Role"}</Divider>
                    <strong>Default Capabilities:</strong>
                    {role.defaultCapabilities.length > 0
                      ? role.defaultCapabilities.map((cap) => (
                          <Tag color="blue" key={cap._id}>
                            {cap.name}
                          </Tag>
                        ))
                      : "None"}{" "}
                    <br />
                    <strong>Default Resources:</strong>
                    {role.defaultRessources.length > 0
                      ? role.defaultRessources.map((res) => (
                          <Tag color="green" key={res._id}>
                            {res.name}
                          </Tag>
                        ))
                      : "None"}
                  </div>
                ))
              : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Overrides">
            {userOverrides.length > 0
              ? userOverrides.map((override, index) => (
                  <div key={index}>
                    <Divider orientation="left">
                      {override.name || "Override"}
                    </Divider>
                    <strong>Default Capabilities:</strong>
                    {override.defaultCapabilities.length > 0
                      ? override.defaultCapabilities.map((cap) => (
                          <Tag color="blue" key={cap._id}>
                            {cap.name}
                          </Tag>
                        ))
                      : "None"}{" "}
                    <br />
                    <strong>Default Resources:</strong>
                    {override.defaultRessources.length > 0
                      ? override.defaultRessources.map((res) => (
                          <Tag color="green" key={res._id}>
                            {res.name}
                          </Tag>
                        ))
                      : "None"}
                  </div>
                ))
              : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Is Active">
            <span style={{ color: item.isActive ? "blue" : "red" }}>
              {item.isActive ? "Yes" : "No"}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Is Deleted">
            {item.isDeleted ? "Yes" : "No"}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {item.createdAt ? new Date(item.createdAt).toLocaleString() : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "N/A"}
          </Descriptions.Item>
        </Descriptions>
      </Drawer>
    </>
  );
};

export default ShowUser;
